/**
 * useQuery — SWR-style data hook over `hooks/cache.ts`.
 *
 * States the UI needs (spec §3.5 / mockup-rules.md):
 *   `loading`  — FIRST load only, with nothing cached → the screen renders skeletons;
 *   `busy`     — a refetch while data is already on screen → the previous render stays,
 *                dimmed to 50% with `aria-busy="true"`, no layout jump, NO skeleton;
 *   `error`    — the last error; when stale data exists it is still rendered with a "לא עודכן" tag.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cacheGet, cacheSet, invalidate, subscribeInvalidate, type CacheScope } from './cache.ts';

export interface QueryResult<T> {
  data: T | undefined;
  error: unknown;
  /** First load with an empty cache → render skeletons. */
  loading: boolean;
  /** Revalidating with data already on screen → dim to 50%, `aria-busy`. */
  busy: boolean;
  refetch: () => void;
  /** Replace the cached value locally (optimistic updates). */
  mutate: (next: T) => void;
}

export interface QueryOptions {
  /** Scopes that invalidate this query (SSE `data:changed`, local mutations). */
  scopes?: readonly CacheScope[];
  /** Skip fetching (e.g. before login). */
  enabled?: boolean;
}

export function useQuery<T>(key: string | null, fetcher: () => Promise<T>, options: QueryOptions = {}): QueryResult<T> {
  const { scopes, enabled = true } = options;
  const [, forceRender] = useState(0);
  const rerender = useCallback(() => forceRender((n) => n + 1), []);

  /**
   * `loading` must already be true on the render that *precedes* the fetching effect, otherwise a
   * consumer that swaps its subtree on `loading` (app.tsx renders `<ScreenSkeleton/>` while the
   * summary is loading) renders the real subtree for one commit, then unmounts it when the effect
   * flips `loading`, then mounts it again when the data lands — every child screen fires its own
   * requests three times. Seeding it from the cache keeps a warm key at `loading: false`.
   */
  const initial = (): { key: string | null; data: T | undefined; error: unknown; loading: boolean; busy: boolean } => {
    const data = key ? cacheGet<T>(key) : undefined;
    return { key, data, error: null, loading: Boolean(key) && enabled && data === undefined, busy: false };
  };

  const stateRef = useRef<{ key: string | null; data: T | undefined; error: unknown; loading: boolean; busy: boolean }>(initial());
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const mountedRef = useRef(true);
  const runIdRef = useRef(0);

  if (stateRef.current.key !== key) stateRef.current = initial();

  const run = useCallback(() => {
    if (!key || !enabled) return;
    const runId = ++runIdRef.current;
    const hasData = stateRef.current.data !== undefined;
    stateRef.current.loading = !hasData;
    stateRef.current.busy = hasData;
    rerender();
    fetcherRef.current().then(
      (data) => {
        if (!mountedRef.current || runId !== runIdRef.current) return;
        cacheSet(key, data);
        stateRef.current = { key, data, error: null, loading: false, busy: false };
        rerender();
      },
      (error: unknown) => {
        if (!mountedRef.current || runId !== runIdRef.current) return;
        stateRef.current = { ...stateRef.current, error, loading: false, busy: false };
        rerender();
      },
    );
  }, [key, enabled, rerender]);

  useEffect(() => {
    mountedRef.current = true;
    if (key && enabled) run();
    return () => {
      mountedRef.current = false;
    };
  }, [key, enabled, run]);

  useEffect(() => {
    if (!scopes || !scopes.length) return undefined;
    return subscribeInvalidate((changed) => {
      if (changed.some((scope) => scopes.includes(scope))) run();
    });
  }, [scopes, run]);

  const mutate = useCallback(
    (next: T) => {
      if (!key) return;
      cacheSet(key, next);
      stateRef.current = { ...stateRef.current, data: next };
      rerender();
    },
    [key, rerender],
  );

  return {
    data: stateRef.current.data,
    error: stateRef.current.error,
    loading: stateRef.current.loading,
    busy: stateRef.current.busy,
    refetch: run,
    mutate,
  };
}

export { invalidate };
